import { useCallback, useMemo, useRef } from 'react'

import { useEvents } from '../hooks/useEvents'

import { Box, Flex } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { throttle } from 'lodash'

import { Spinner } from '@/components/ui'

import { EventFiltersI } from '../types/types'
import { EventItem } from './EventItem'
import { TRACE_LOG_PANEL_HEIGHT } from './TraceLogContent'

const onEndReachedThreshold = 0.95

interface EventFeedPropsI extends EventFiltersI {
  showOnlyIncidentRelatedEvents?: boolean
}

export const EventFeed = ({
  incident,
  filters,
  showOnlyIncidentRelatedEvents = false,
}: EventFeedPropsI) => {
  const ref = useRef<HTMLDivElement>(null)
  const { isLoading, events, getNewerEvents, getOlderEvents, hasOlder } =
    useEvents({
      incident,
      filters,
    })

  const onScroll = useCallback(
    throttle(async () => {
      if (ref?.current) {
        const { scrollTop, scrollHeight, clientHeight } = ref.current

        // Scrolled to the top
        if (scrollTop <= 0) {
          const hasMoreEvents = await getNewerEvents()
          if (hasMoreEvents) {
            ref?.current?.scrollTo({ top: 50, behavior: 'auto' })
          }
        }
        // Scrolled to the bottom
        const scrollPosition = scrollTop + clientHeight
        const endScrollThreshold = scrollHeight * onEndReachedThreshold
        if (scrollPosition >= endScrollThreshold && hasOlder) {
          await getOlderEvents()
        }
      }
    }, 200),
    [getNewerEvents]
  )

  const eventExternalIdMatchesIncident = (eventExternalId: string) => {
    return incident?.eventExternalIds?.includes(eventExternalId) ?? false
  }

  // Filter events to show only incident-related events when requested (for Incident tab)
  const filteredEvents = useMemo(() => {
    if (!showOnlyIncidentRelatedEvents || !incident?.eventExternalIds) {
      return events
    }
    return events?.filter((e) =>
      incident.eventExternalIds.includes(e.node.externalId)
    )
  }, [events, showOnlyIncidentRelatedEvents, incident?.eventExternalIds])

  return (
    <Flex
      borderTop='1px solid'
      borderTopColor='border.200'
      direction='column'
      gap='8px'
      height={TRACE_LOG_PANEL_HEIGHT}
      onScroll={onScroll}
      overflowX='hidden'
      overflowY='auto'
      paddingInline='24px'
      paddingTop='24px'
      ref={ref}
    >
      <AnimatePresence>
        {filteredEvents?.map((e) => (
          <motion.div
            animate={{ x: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            initial={{ x: -200, opacity: 0 }}
            key={e.node.externalId}
          >
            <EventItem
              event={e.node}
              isCauseOfIncident={eventExternalIdMatchesIncident(
                e.node.externalId
              )}
              key={e.node?.externalId}
              shouldShowDeviceInfo={Boolean(filters?.facilityId)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
      {isLoading && (
        <Box
          alignContent='center'
          backgroundColor='white'
          borderColor='border.200'
          borderRadius='10px'
          borderWidth='1px'
          height='80px'
          left='calc(50% - 40px)'
          pointerEvents='none'
          position='absolute'
          top='calc(50% - 40px)'
          width='80px'
        >
          <Spinner size='lg' />
        </Box>
      )}
    </Flex>
  )
}
